import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Snackbar from '@mui/material/Snackbar';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router';
import { Alert, TextV2 } from '@insights-ltd/design-library/components';
import {
  EventResponseWithChapters,
  InviteeResponse,
  useDeleteInvitee,
} from 'api';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { isExploreExperience, isIdtlExperience } from 'domain/event';
import { INVITEE_STATUSES } from 'variables';
import DeleteLearnerDialog from 'pages/EventLearners/DeleteLearnerDialog';
import { InviteeListOptions } from 'types/types';
import LearnersStatusList from './LearnersStatusList';
import LearnersSearchBox from './LearnersSearchBox';
import LearnersStatusFilter from './LearnersStatusFilter';

type LearnersListProps = {
  invitees: InviteeResponse[];
  event: EventResponseWithChapters;
  isPastExperience: boolean;
};

const LearnersList = ({
  invitees,
  event,
  isPastExperience,
}: LearnersListProps) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<InviteeListOptions | ''>('');
  const { mutate, isPending, isError, reset } = useDeleteInvitee();
  const [inviteeId, setInviteeId] = useState('');
  const isDiscoveryExperience =
    !isIdtlExperience(event?.eventType) &&
    !isExploreExperience(event?.eventType);

  const sortedReadyToPurchaseInvitees = invitees
    .filter(
      (inviteeResponse: InviteeResponse) =>
        inviteeResponse.status === 'READY_TO_PURCHASE' &&
        !inviteeResponse.profile,
    )
    .sort((a: InviteeResponse, b: InviteeResponse) => {
      return (
        Date.parse(b.evaluation?.submittedOn || '') -
        Date.parse(a.evaluation?.submittedOn || '')
      );
    });
  const unsortedInvitees = invitees.filter(
    (inviteeResponse: InviteeResponse) =>
      !(
        inviteeResponse.status === 'READY_TO_PURCHASE' &&
        !inviteeResponse.profile
      ),
  );

  const sortedInvitees = unsortedInvitees.concat(sortedReadyToPurchaseInvitees);

  const filteredInvitees =
    searchText === ''
      ? sortedInvitees
      : sortedInvitees.filter(
          (invitee) =>
            invitee.fullName.toLowerCase().includes(searchText.toLowerCase()) ||
            invitee.email.toLowerCase().includes(searchText.toLowerCase()),
        );
  const inviteesCount = sortedInvitees.length;
  const filteredCount = filteredInvitees.length;

  const handleDeleteInvitee = async (selectedInviteeId: string) => {
    setInviteeId(selectedInviteeId);
  };

  const handleConfirmDelete = () =>
    mutate(
      {
        eventId: event.id,
        inviteeId,
      },
      { onSuccess: () => setInviteeId('') },
    );

  return (
    <>
      {inviteesCount > 0 ? (
        <>
          <TextV2 component="h2" variant="h3">
            {t('ui.event-management.events.learners-list.heading.learners')} (
            {inviteesCount > filteredCount
              ? `${filteredCount}/${inviteesCount}`
              : inviteesCount}
            )
          </TextV2>
          <Box
            sx={{
              mt: (theme) => theme.spacing(spacingSizeMap.S),
            }}
          />
          <Grid
            container
            spacing={2}
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Grid size={{ xs: 12, md: 4 }}>
              <LearnersSearchBox value={searchText} onChange={setSearchText} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <LearnersStatusFilter
                onChange={setStatusFilter}
                value={statusFilter}
              />
            </Grid>
          </Grid>
          <Box
            sx={{
              mt: (theme) => theme.spacing(spacingSizeMap.M),
            }}
          />
          <Grid
            spacing={1}
            container
            sx={(theme) => ({
              padding: '8px 20px 8px 24px',
              color: theme.palette.grey[600],
              backgroundColor: theme.palette.grey[300],
              margin: '0 0 0 -8px',
            })}
          >
            <Grid sx={{ marginRight: '18px' }} size={3}>
              <TextV2 variant="bodyBold">
                {t('ui.event-management.events.list.heading.full-name-email')}
              </TextV2>
            </Grid>
            {isDiscoveryExperience && (
              <Grid sx={{ marginLeft: '1rem' }} size={4}>
                <TextV2 variant="bodyBold">
                  {t(
                    'ui.event-management.events.learners-list.heading.chapters',
                  )}
                  &nbsp;&nbsp;
                  {inviteesCount > 0 && !isPastExperience && (
                    <TextV2 component="span" variant="bodyBold" color="primary">
                      <Link
                        component={RouterLink}
                        to={`/experiences/${event.id}/assign-chapters`}
                      >
                        {t(
                          'ui.event-management.events.learners-list.assign-link-label',
                        )}
                      </Link>
                    </TextV2>
                  )}
                </TextV2>
              </Grid>
            )}
          </Grid>
          {Object.keys(INVITEE_STATUSES).map((status) => (
            <LearnersStatusList
              key={status}
              listStatus={status as InviteeListOptions}
              statusFilter={statusFilter}
              invitees={filteredInvitees}
              deleteInvitee={handleDeleteInvitee}
              event={event}
            />
          ))}
        </>
      ) : null}
      <DeleteLearnerDialog
        open={Boolean(inviteeId)}
        onClose={() => setInviteeId('')}
        handleConfirmDelete={handleConfirmDelete}
        deleting={isPending}
      />
      <Snackbar open={isError} autoHideDuration={6000} onClose={reset}>
        <Alert elevation={6} variant="filled" onClose={reset} severity="error">
          {t('ui.event-management.events.invitee.error-deleting-invitee')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default LearnersList;
